import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Database,
  GitMerge,
  Layers,
  Library,
  Radio,
} from 'lucide-react';

import { NAV_ITEMS } from '@/app/navigation';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dashboardKeys, fetchSourcesSummary } from '@/features/dashboard/api';
import {
  AreaChart,
  BarList,
  Donut,
  KpiCard,
  formatCompact,
  formatNumber,
  type BarItem,
  type DonutSegment,
} from '@/features/dashboard/widgets';
import { AnomalyStatusPill } from '@/features/quality/components/AnomalyStatusPill';
import { metricLabel } from '@/features/quality/labels';
import { useAnomalies, useMetricSeries } from '@/features/quality/hooks';
import type { QualityAnomaly } from '@/features/quality/types';
import { useClusters } from '@/features/mdm/hooks';

const PALETTE = ['brand', 'sky', 'violet', 'amber', 'crimson'] as const;

function fmtMetric(v: number): string {
  return Math.abs(v) < 1 && v !== 0 ? v.toFixed(3) : formatNumber(v);
}

function baselineOf(a: QualityAnomaly): number | null {
  const [low, high] = a.expected_range;
  if (low !== null && high !== null) return (low + high) / 2;
  return low ?? high ?? null;
}

function deviationOf(a: QualityAnomaly): number | null {
  const base = baselineOf(a);
  if (base === null) return null;
  if (base === 0) return a.value === 0 ? 0 : null;
  return ((a.value - base) / Math.abs(base)) * 100;
}

function confidenceTier(c: number): { label: string; cls: string } {
  if (c >= 0.8) return { label: 'HIGH', cls: 'text-accent-crimson' };
  if (c >= 0.5) return { label: 'MEDIUM', cls: 'text-accent-amber' };
  return { label: 'LOW', cls: 'text-ink-500 dark:text-ink-400' };
}

export function DashboardPage(): JSX.Element {
  const sources = useQuery({ queryKey: dashboardKeys.sources, queryFn: fetchSourcesSummary });
  const anomaliesQ = useAnomalies({});
  const clustersQ = useClusters({});

  const connectors = sources.data?.connectors ?? [];
  const datasets = sources.data?.datasets ?? [];
  const anomalies = anomaliesQ.data ?? [];
  const clusters = clustersQ.data ?? [];

  const totalRecords = datasets.reduce((s, d) => s + (d.row_count ?? 0), 0);
  const openAnomalies = anomalies.filter((a) => a.status === 'open').length;
  const openClusters = clusters.filter((c) => c.status === 'open').length;

  const recordBars: BarItem[] = [...datasets]
    .sort((a, b) => (b.row_count ?? 0) - (a.row_count ?? 0))
    .slice(0, 6)
    .map((d, i) => ({
      label: d.name,
      value: d.row_count ?? 0,
      display: formatCompact(d.row_count ?? 0),
      accent: PALETTE[i % PALETTE.length],
    }));

  const statusCounts = { open: 0, acknowledged: 0, resolved: 0 };
  anomalies.forEach((a) => (statusCounts[a.status] += 1));
  const donutSegments: DonutSegment[] = [
    { label: 'Open', value: statusCounts.open, accent: 'crimson' },
    { label: 'Acknowledged', value: statusCounts.acknowledged, accent: 'amber' },
    { label: 'Resolved', value: statusCounts.resolved, accent: 'brand' },
  ];

  const topAnomaly = [...anomalies].sort((a, b) => b.confidence - a.confidence)[0];
  const trend = useMetricSeries(topAnomaly?.asset_fqn ?? '', topAnomaly?.metric ?? 'row_count', !!topAnomaly);
  const markerTimes = new Set(
    anomalies
      .filter((a) => topAnomaly && a.asset_fqn === topAnomaly.asset_fqn && a.metric === topAnomaly.metric)
      .map((a) => new Date(a.ts).getTime()),
  );

  const modules = NAV_ITEMS.filter((n) => n.group === 'modules');

  if (sources.isLoading && !sources.data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner label="Loading dashboard…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow eyebrow-dot mb-2.5">
            <span>OVERVIEW</span>
          </div>
          <h1 className="h-display">Dashboard</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
            Live health of your data estate — catalogue coverage, quality, and master-data hygiene at a glance.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-700 ring-1 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/20">
          <Radio className="h-3 w-3" />
          Live
        </span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard
          label="Data Sources"
          value={formatNumber(connectors.length)}
          sub="connected across the estate"
          icon={Database}
          accent="sky"
          to="/connectors"
        />
        <KpiCard
          label="Datasets Catalogued"
          value={formatNumber(datasets.length)}
          sub={`across ${connectors.length} source${connectors.length === 1 ? '' : 's'}`}
          icon={Library}
          accent="brand"
          to="/connectors"
        />
        <KpiCard
          label="Total Records"
          value={formatNumber(totalRecords)}
          sub={`in ${datasets.length} dataset${datasets.length === 1 ? '' : 's'}`}
          icon={Layers}
          accent="violet"
          to="/connectors"
        />
        <KpiCard
          label="Open Anomalies"
          value={formatNumber(openAnomalies)}
          sub={`${anomalies.length} detected total`}
          icon={AlertTriangle}
          accent="crimson"
          alert={openAnomalies > 0}
          to="/quality"
        />
        <KpiCard
          label="Duplicate Groups"
          value={formatNumber(openClusters)}
          sub={`${clusters.length} resolved by AI`}
          icon={GitMerge}
          accent="amber"
          to="/mdm"
        />
      </div>

      {/* Records by dataset + anomaly status */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Records by dataset" description="Row volume across catalogued datasets." to="/connectors">
          {recordBars.length > 0 ? (
            <BarList items={recordBars} />
          ) : (
            <p className="py-6 text-center text-sm text-ink-400 dark:text-ink-500">No datasets yet.</p>
          )}
        </Card>

        <section className="flex flex-col rounded-xl border border-ink-200/70 bg-white p-5 shadow-soft transition hover:shadow-elevated dark:border-white/10 dark:bg-ink-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
                Anomalies by status
              </h2>
              <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">Where the quality queue stands.</p>
            </div>
            <Link
              to="/quality"
              className="group inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
            >
              View
              <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </Link>
          </div>
          <div className="flex flex-1 items-center justify-center pt-4">
            {anomalies.length > 0 ? (
              <Donut segments={donutSegments} centerValue={formatNumber(anomalies.length)} centerLabel="total" />
            ) : (
              <p className="text-sm text-ink-400 dark:text-ink-500">No anomalies detected.</p>
            )}
          </div>
        </section>
      </div>

      {/* Anomaly trend */}
      {topAnomaly && (
        <Card
          title="Metric trend"
          description={`${topAnomaly.asset_fqn} · ${metricLabel(topAnomaly.metric)} — red dots mark flagged issues.`}
          to="/quality"
        >
          {trend.isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <AreaChart
              points={trend.data ?? []}
              markerTimes={markerTimes}
              accent={topAnomaly.metric === 'null_rate' ? 'amber' : 'sky'}
            />
          )}
        </Card>
      )}

      {/* AI anomaly detections table */}
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
              AI anomaly detections
            </h2>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Statistically scored deviations across profiled metrics.
            </p>
          </div>
          <Link
            to="/quality"
            className="group inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-600 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
          >
            {anomalies.length} detected
            <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
          </Link>
        </div>

        {anomalies.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-400 dark:text-ink-500">
            No anomalies — run detection from the Data Quality module.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-400 dark:border-white/10 dark:text-ink-500">
                  <th className="py-2 pr-4 font-medium">Field</th>
                  <th className="py-2 pr-4 font-medium">Metric</th>
                  <th className="py-2 pr-4 text-right font-medium">Baseline</th>
                  <th className="py-2 pr-4 text-right font-medium">Current</th>
                  <th className="py-2 pr-4 text-right font-medium">Deviation</th>
                  <th className="py-2 pr-4 font-medium">Confidence</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-white/5">
                {anomalies.slice(0, 8).map((a) => {
                  const base = baselineOf(a);
                  const dev = deviationOf(a);
                  const conf = confidenceTier(a.confidence);
                  const devCls =
                    dev === null
                      ? 'text-ink-400 dark:text-ink-500'
                      : Math.abs(dev) >= 50
                        ? 'text-accent-crimson'
                        : Math.abs(dev) >= 20
                          ? 'text-accent-amber'
                          : 'text-ink-600 dark:text-ink-300';
                  return (
                    <tr key={a.id} className="text-ink-700 dark:text-ink-200">
                      <td className="py-2.5 pr-4 font-mono text-xs text-ink-800 dark:text-ink-100">{a.asset_fqn}</td>
                      <td className="py-2.5 pr-4 text-ink-600 dark:text-ink-300">{metricLabel(a.metric)}</td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs text-ink-500 dark:text-ink-400">
                        {base === null ? '—' : fmtMetric(base)}
                      </td>
                      <td className="py-2.5 pr-4 text-right font-mono text-xs font-medium text-ink-800 dark:text-ink-100">
                        {fmtMetric(a.value)}
                      </td>
                      <td className={`py-2.5 pr-4 text-right font-mono text-xs font-semibold ${devCls}`}>
                        {dev === null ? '—' : `${dev >= 0 ? '+' : ''}${dev.toFixed(1)}%`}
                      </td>
                      <td className={`py-2.5 pr-4 font-mono text-[11px] font-semibold ${conf.cls}`}>{conf.label}</td>
                      <td className="py-2.5">
                        <AnomalyStatusPill status={a.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Module quick links */}
      <div className="grid gap-3 sm:grid-cols-3">
        {modules.map((m) => (
          <Link
            key={m.key}
            to={m.path}
            className="group flex items-center justify-between rounded-xl border border-ink-200/70 bg-white px-4 py-3 shadow-soft transition hover:-translate-y-0.5 hover:shadow-elevated dark:border-white/10 dark:bg-ink-900"
          >
            <div>
              <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">{m.label}</div>
              <div className="text-xs text-ink-500 dark:text-ink-400">{m.description}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-ink-400 transition group-hover:translate-x-0.5 group-hover:text-brand-600 dark:text-ink-500" />
          </Link>
        ))}
      </div>
    </div>
  );
}
